/**
 * DXF 导出适配层 —— 将 CadHandle + CadExportOptions 转换为 DxfExporter 原始调用
 */

import type { ICADMetadata } from '@/plugins/cad/module/CADModule/manager/DataManager'
import { DxfExporter } from '@/plugins/cad/module/exporter/dxfExporter/DxfExporter'
import type { CadExportOptions } from '@/plugins/cad/types'
import { getLoggerManager } from '@/shared/logging'
import type { AssetExporter, ExportOptions } from '@/shared/types/assets'
import type { CadHandle } from '@/shared/types/services'

const log = getLoggerManager().getLogger('cad:export')

/** 元数据获取函数类型 */
export type MetadataProvider = (handle: CadHandle) => ICADMetadata

/** DXF 格式 CAD 导出适配器 */
export class DxfCadExporter implements AssetExporter<CadHandle, Blob> {
  /** 导出器支持的格式标识 */
  readonly format = 'dxf'

  /** 外部注入的元数据获取函数 */
  private readonly getMetadata: MetadataProvider

  constructor(getMetadata: MetadataProvider) {
    this.getMetadata = getMetadata
  }

  /**
   * 将 CadHandle 导出为 DXF Blob。
   *
   * @param handle - 待导出的 CAD 资源句柄
   * @param options - 导出选项，可在 extra.units 中指定单位
   * @returns DXF 内容 Blob，MIME 为 application/dxf
   */
  async export(handle: CadHandle, options?: ExportOptions): Promise<Blob> {
    const exportStart = getLoggerManager().now()
    // 获取序列化元数据
    const metadata = this.getMetadata(handle)

    const cadOptions = options as CadExportOptions | undefined
    const { displayUnit, feetFormat, units } = cadOptions?.extra ?? {}

    // 委托底层 DxfExporter 生成 DXF 字符串
    const dxfString =
      displayUnit !== undefined || feetFormat !== undefined
        ? DxfExporter.export(metadata, { displayUnit, feetFormat })
        : units !== undefined
          ? DxfExporter.export(metadata, units)
          : DxfExporter.export(metadata)
    log.debug(
      `[DxfCadExporter] dxf generated, id=${handle.id}, entities=${metadata.entities.length}, layers=${metadata.layers.length}, chars=${dxfString.length}, elapsed=${(getLoggerManager().now() - exportStart).toFixed(1)}ms`
    )

    // 转换为 Blob
    return new Blob([dxfString], { type: 'application/dxf' })
  }
}
